import { odataGet, odataPost, odataPatch, odataEscape } from './dataverse';
import type { PolicyContext } from './datahub';

/**
 * Compose data-access + SODAN rendering. Talks to the gaqc_* library and
 * per-record tables. See dataverse-setup/schema.json for the model.
 */

// ===== Library types =====
export type Template = {
  gaqc_templateid?: string;
  gaqc_templateref: string;
  gaqc_name?: string;
  gaqc_kind?: string;
  gaqc_producttype?: string;
  gaqc_body?: string;
  gaqc_version?: number;
  gaqc_status?: string;
  gaqc_description?: string;
};

export type ProductDN = {
  gaqc_product_dnid?: string;
  gaqc_dnref: string;
  gaqc_name?: string;
  gaqc_producttype?: string;
  gaqc_demandtext?: string;
  gaqc_sortorder?: number;
  gaqc_status?: string;
};

export type Addon = {
  gaqc_addonid?: string;
  gaqc_addonref: string;
  gaqc_name?: string;
  gaqc_producttype?: string;
  gaqc_description?: string;
  gaqc_sortorder?: number;
  gaqc_status?: string;
};

export type AddonRationale = {
  gaqc_addon_dnid?: string;
  gaqc_addondnref: string;
  gaqc_name?: string;
  gaqc_addonref?: string;
  gaqc_decisionkind?: string; // Take | Decline
  gaqc_rationale?: string;
  gaqc_sortorder?: number;
  gaqc_status?: string;
};

export type BasisOption = {
  gaqc_basisid?: string;
  gaqc_basisref: string;
  gaqc_name?: string;
  gaqc_producttype?: string; // 'General' or a specific product
  gaqc_category?: string;
  gaqc_basistext?: string;
  gaqc_sortorder?: number;
  gaqc_status?: string;
};

// ===== Per-record types =====
export type AddonDecisionValue = 'Taken' | 'Declined' | 'NA';

export type PolicyAssessment = {
  gaqc_policyassessmentid?: string;
  gaqc_policyassessmentref: string;
  gaqc_policyyearref?: string;
  gaqc_contactref?: string;
  gaqc_clientname?: string;
  gaqc_producttype?: string;
  gaqc_policydescription?: string;
  gaqc_dnselectedjson?: string;
  gaqc_recommendation?: string;
  gaqc_justification?: string;
  gaqc_status?: string;
  gaqc_createdbyupn?: string;
  gaqc_createdatutc?: string;
  gaqc_modifiedbyupn?: string;
  gaqc_modifiedatutc?: string;
};

export type Correspondence = {
  gaqc_correspondenceid?: string;
  gaqc_correspondenceref: string;
  gaqc_kind?: string;
  gaqc_policyyearref?: string;
  gaqc_policyassessmentref?: string;
  gaqc_contactref?: string;
  gaqc_clientname?: string;
  gaqc_templateref?: string;
  gaqc_renderedbody?: string;
  gaqc_status?: string;
  gaqc_copiedtoacturisatutc?: string;
  gaqc_copiedtoacturisbyupn?: string;
  gaqc_createdbyupn?: string;
  gaqc_createdatutc?: string;
};

// ===== Library reads =====
export async function fetchActiveTemplate(kind: string, productType: string): Promise<Template | null> {
  const r = await odataGet<Template>(
    'gaqc_templates',
    `?$filter=gaqc_kind eq '${odataEscape(kind)}' and gaqc_producttype eq '${odataEscape(productType)}' and gaqc_status eq 'Active'` +
      `&$select=gaqc_templateref,gaqc_name,gaqc_kind,gaqc_producttype,gaqc_body,gaqc_version&$orderby=gaqc_version desc&$top=1`
  );
  return r.value?.[0] ?? null;
}

export async function fetchDemandsAndNeeds(productType: string): Promise<ProductDN[]> {
  const r = await odataGet<ProductDN>(
    'gaqc_product_dns',
    `?$filter=gaqc_producttype eq '${odataEscape(productType)}' and gaqc_status eq 'Active'` +
      `&$select=gaqc_dnref,gaqc_name,gaqc_demandtext,gaqc_sortorder&$orderby=gaqc_sortorder asc&$top=100`
  );
  return r.value ?? [];
}

export async function fetchAddons(productType: string): Promise<Addon[]> {
  const r = await odataGet<Addon>(
    'gaqc_addons',
    `?$filter=gaqc_producttype eq '${odataEscape(productType)}' and gaqc_status eq 'Active'` +
      `&$select=gaqc_addonref,gaqc_name,gaqc_description,gaqc_sortorder&$orderby=gaqc_sortorder asc&$top=100`
  );
  return r.value ?? [];
}

export async function fetchAddonRationales(): Promise<AddonRationale[]> {
  // All Active rationales. We over-fetch by status only (cheap — rationale
  // rows link by addonref, not product) and index client-side by
  // addonref+decisionkind. The caller filters to the product's add-ons.
  const r = await odataGet<AddonRationale>(
    'gaqc_addon_dns',
    `?$filter=gaqc_status eq 'Active'` +
      `&$select=gaqc_addondnref,gaqc_addonref,gaqc_decisionkind,gaqc_rationale,gaqc_sortorder&$orderby=gaqc_sortorder asc&$top=500`
  );
  return r.value ?? [];
}

/**
 * Active basis-of-recommendation snippets offered for a product: the
 * 'General' set (applies to everything) plus any specific to this product.
 * Draft-status rows (staged content for products not yet built) are excluded.
 */
export async function fetchBasisOptions(productType: string): Promise<BasisOption[]> {
  const r = await odataGet<BasisOption>(
    'gaqc_basiss',
    `?$filter=gaqc_status eq 'Active' and (gaqc_producttype eq 'General' or gaqc_producttype eq '${odataEscape(productType)}')` +
      `&$select=gaqc_basisref,gaqc_name,gaqc_producttype,gaqc_category,gaqc_basistext,gaqc_sortorder&$orderby=gaqc_sortorder asc&$top=500`
  );
  // Product-specific first, then General — both already sorted by sortorder.
  const rows = r.value ?? [];
  const specific = rows.filter((b) => b.gaqc_producttype !== 'General');
  const general = rows.filter((b) => b.gaqc_producttype === 'General');
  return [...specific, ...general];
}

/** Index rationales as { addonref: { Take: text, Decline: text } } (first Active wins). */
export function indexRationales(rows: AddonRationale[]): Record<string, { Take?: string; Decline?: string }> {
  const idx: Record<string, { Take?: string; Decline?: string }> = {};
  for (const r of rows) {
    const ref = r.gaqc_addonref ?? '';
    if (!ref) continue;
    idx[ref] ??= {};
    const kind = r.gaqc_decisionkind;
    if (kind === 'Take' && idx[ref].Take == null) idx[ref].Take = r.gaqc_rationale ?? '';
    if (kind === 'Decline' && idx[ref].Decline == null) idx[ref].Decline = r.gaqc_rationale ?? '';
  }
  return idx;
}

// ===== SODAN rendering =====
export type AddonDecisionInput = {
  addonref: string;
  addonName: string;
  decision: AddonDecisionValue;
  rationale: string;
};

export type RenderInput = {
  ctx: PolicyContext;
  brokerName: string;
  today: string;
  selectedDN: ProductDN[];
  addonDecisions: AddonDecisionInput[];
  recommendation: string;
  /** Selected standard basis-of-recommendation snippets, in display order. */
  selectedBasis: BasisOption[];
  /** Optional free-text the broker adds on top of the selected basis snippets. */
  justification: string;
};

/** Build the bullet block for selected demands & needs. */
function renderDNBlock(dn: ProductDN[]): string {
  if (!dn.length) return '(none selected)';
  return dn.map((d) => `- ${d.gaqc_demandtext ?? d.gaqc_name ?? ''}`).join('\n');
}

/** Build the add-on decisions block (skips N/A by default but lists Taken/Declined). */
function renderAddonBlock(decisions: AddonDecisionInput[]): string {
  const shown = decisions.filter((d) => d.decision !== 'NA');
  if (!shown.length) return '(no add-ons recorded)';
  return shown
    .map((d) => {
      const verb = d.decision === 'Taken' ? 'Taken' : 'Declined';
      const rationale = d.rationale ? ` — ${d.rationale}` : '';
      return `- ${d.addonName}: ${verb}${rationale}`;
    })
    .join('\n');
}

const PLACEHOLDER_FALLBACK = '—';

/**
 * Compose the "basis of recommendation" block: the selected standard
 * snippets (each on its own line) followed by any broker free-text. Each
 * snippet's own text may itself be multi-line (bullet blocks), which we
 * preserve verbatim.
 */
function renderBasisBlock(selected: BasisOption[], freeText: string): string {
  const lines = selected.map((b) => (b.gaqc_basistext ?? '').trim()).filter(Boolean);
  const extra = (freeText ?? '').trim();
  if (extra) lines.push(extra);
  return lines.length ? lines.join('\n') : PLACEHOLDER_FALLBACK;
}

/**
 * Render a template body by substituting {{placeholders}}. Unknown
 * placeholders are left as-is so authoring mistakes are visible rather
 * than silently blanked.
 */
export function renderTemplate(body: string, input: RenderInput): string {
  const map: Record<string, string> = {
    clientName: input.ctx.clientName || PLACEHOLDER_FALLBACK,
    clientAddress: input.ctx.clientAddress || PLACEHOLDER_FALLBACK,
    productType: input.ctx.productType || PLACEHOLDER_FALLBACK,
    policyDescription: input.ctx.policyDescription || PLACEHOLDER_FALLBACK,
    brokerName: input.brokerName || PLACEHOLDER_FALLBACK,
    today: input.today,
    premium: input.ctx.premium || PLACEHOLDER_FALLBACK,
    insurer: input.ctx.insurer || PLACEHOLDER_FALLBACK,
    demandsAndNeeds: renderDNBlock(input.selectedDN),
    addonDecisions: renderAddonBlock(input.addonDecisions),
    recommendation: input.recommendation || PLACEHOLDER_FALLBACK,
    justification: renderBasisBlock(input.selectedBasis, input.justification),
  };
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, key: string) =>
    key in map ? map[key] : whole
  );
}

// ===== ref generation =====
// Stable, sortable refs. NNNNN from epoch-seconds mod 100000 + an alt-key
// guard on the table catches the rare clash on re-POST.
function serial(): string {
  return String(Math.floor(Date.now() / 1000) % 100000).padStart(5, '0');
}
export function genPolicyAssessmentRef(): string {
  return `PA-${new Date().getFullYear()}-${serial()}`;
}
export function genAddonDecisionRef(): string {
  return `AD-${new Date().getFullYear()}-${serial()}`;
}
export function genCorrespondenceRef(): string {
  return `CO-${new Date().getFullYear()}-${serial()}`;
}

// ===== Saves =====
export type SaveSodanInput = {
  ctx: PolicyContext;
  productType: string;
  selectedDNRefs: string[];
  selectedBasisRefs: string[];
  recommendation: string;
  justification: string;
  addonDecisions: AddonDecisionInput[];
  templateRef: string;
  renderedBody: string;
  upn: string;
};

export type SaveSodanResult = {
  policyAssessmentRef: string;
  correspondenceRef: string;
};

/**
 * Persist a SODAN draft: one gaqc_policyassessment + N gaqc_addondecision
 * + one gaqc_correspondence. Sequential (small N) so a failure surfaces
 * clearly; the policyassessmentref string-links the children.
 */
export async function saveSodan(input: SaveSodanInput): Promise<SaveSodanResult> {
  const nowIso = new Date().toISOString();
  const paRef = genPolicyAssessmentRef();

  await odataPost('gaqc_policyassessments', {
    gaqc_policyassessmentref: paRef,
    gaqc_name: `${input.ctx.clientName} — ${input.productType}`,
    gaqc_policyyearref: input.ctx.policyYearRef,
    gaqc_contactref: input.ctx.contactRef,
    gaqc_clientname: input.ctx.clientName,
    gaqc_producttype: input.productType,
    gaqc_policydescription: input.ctx.policyDescription,
    gaqc_dnselectedjson: JSON.stringify(input.selectedDNRefs),
    gaqc_basisselectedjson: JSON.stringify(input.selectedBasisRefs),
    gaqc_recommendation: input.recommendation,
    gaqc_justification: input.justification,
    gaqc_status: 'Complete',
    gaqc_createdbyupn: input.upn,
    gaqc_createdatutc: nowIso,
    gaqc_modifiedbyupn: input.upn,
    gaqc_modifiedatutc: nowIso,
  });

  let order = 0;
  for (const d of input.addonDecisions) {
    await odataPost('gaqc_addondecisions', {
      gaqc_addondecisionref: genAddonDecisionRef() + '-' + order,
      gaqc_name: `${paRef} — ${d.addonName} — ${d.decision}`,
      gaqc_policyassessmentref: paRef,
      gaqc_addonref: d.addonref,
      gaqc_addonname: d.addonName,
      gaqc_decision: d.decision,
      gaqc_rationale: d.rationale,
      gaqc_sortorder: order,
    });
    order += 1;
  }

  const coRef = genCorrespondenceRef();
  await odataPost('gaqc_correspondences', {
    gaqc_correspondenceref: coRef,
    gaqc_name: `SODAN — ${input.ctx.clientName} — ${input.productType}`,
    gaqc_kind: 'SODAN',
    gaqc_policyyearref: input.ctx.policyYearRef,
    gaqc_policyassessmentref: paRef,
    gaqc_contactref: input.ctx.contactRef,
    gaqc_clientname: input.ctx.clientName,
    gaqc_templateref: input.templateRef,
    gaqc_renderedbody: input.renderedBody,
    gaqc_status: 'Draft',
    gaqc_createdbyupn: input.upn,
    gaqc_createdatutc: nowIso,
  });

  return { policyAssessmentRef: paRef, correspondenceRef: coRef };
}

/** Fetch recent SODAN correspondence for the list view. */
export async function fetchRecentCorrespondence(): Promise<Correspondence[]> {
  const r = await odataGet<Correspondence>(
    'gaqc_correspondences',
    `?$select=gaqc_correspondenceref,gaqc_kind,gaqc_policyyearref,gaqc_clientname,gaqc_status,gaqc_copiedtoacturisatutc,gaqc_createdbyupn,gaqc_createdatutc&$orderby=gaqc_createdatutc desc&$top=200`
  );
  return r.value ?? [];
}

/** Fetch one correspondence row by ref (for the detail view). */
export async function fetchCorrespondence(ref: string): Promise<Correspondence | null> {
  const r = await odataGet<Correspondence>(
    'gaqc_correspondences',
    `?$filter=gaqc_correspondenceref eq '${odataEscape(ref)}'&$top=1`
  );
  return r.value?.[0] ?? null;
}

/**
 * Mark a correspondence as copied into Acturis (MVP — manual button,
 * Andy's decision 2026-05-31). Records who + when and flips status.
 */
export async function markCopiedToActuris(co: Correspondence, upn: string): Promise<void> {
  if (!co.gaqc_correspondenceid) throw new Error('Correspondence id missing');
  await odataPatch('gaqc_correspondences', co.gaqc_correspondenceid, {
    gaqc_status: 'CopiedToActuris',
    gaqc_copiedtoacturisatutc: new Date().toISOString(),
    gaqc_copiedtoacturisbyupn: upn,
  });
}

// ===== Library admin writes (probe-then-PATCH/POST upsert by ref) =====
async function upsertByRef(
  entitySet: string,
  refColumn: string,
  refValue: string,
  idColumn: string,
  body: Record<string, unknown>
): Promise<void> {
  const found = await odataGet<Record<string, string>>(
    entitySet,
    `?$select=${idColumn}&$filter=${refColumn} eq '${odataEscape(refValue)}'&$top=1`
  );
  if (found.value?.length) {
    await odataPatch(entitySet, found.value[0][idColumn], body);
  } else {
    await odataPost(entitySet, body);
  }
}

export const saveDN = (dn: ProductDN) =>
  upsertByRef('gaqc_product_dns', 'gaqc_dnref', dn.gaqc_dnref, 'gaqc_product_dnid', dn as Record<string, unknown>);

export const saveAddon = (a: Addon) =>
  upsertByRef('gaqc_addons', 'gaqc_addonref', a.gaqc_addonref, 'gaqc_addonid', a as Record<string, unknown>);

export const saveAddonRationale = (r: AddonRationale) =>
  upsertByRef('gaqc_addon_dns', 'gaqc_addondnref', r.gaqc_addondnref, 'gaqc_addon_dnid', r as Record<string, unknown>);

export const saveTemplate = (t: Template) =>
  upsertByRef('gaqc_templates', 'gaqc_templateref', t.gaqc_templateref, 'gaqc_templateid', t as Record<string, unknown>);
